// services/patient/patient-legal-acceptance.service.ts
import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, tap, of, map, catchError } from 'rxjs';
import { PatientLegalAcceptanceEndpoints } from '@medicalapp/shared-core';
import {
  IAcceptLegalDocumentsRequest,
  IActiveLegalDocument,
  IPatientLegalAcceptanceStatus,
} from '@models/patient/patient-legal-acceptance.model';
import { ApiResponse } from '@models/response.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class PatientLegalAcceptanceService {
  // Cache — cleared on logout
  private _acceptanceChecked = signal(false);
  private _hasPending        = signal(false);

  constructor(private httpClient: HttpClient) {}

  getMyLegalAcceptance = (): Observable<ApiResponse<IPatientLegalAcceptanceStatus[]>> => {
    return this.httpClient.get<ApiResponse<IPatientLegalAcceptanceStatus[]>>(
      PatientLegalAcceptanceEndpoints.LegalAcceptance
    );
  };

  // Used by guard — cached after first check
  checkPendingAcceptance = (): Observable<boolean> => {
    if (this._acceptanceChecked()) {
      return of(this._hasPending());
    }

    return this.getMyLegalAcceptance().pipe(
      map((response: ApiResponse<IPatientLegalAcceptanceStatus[]>) => {
        const records = response?.data ?? [];
        const hasPending = records.some(
          (r: IPatientLegalAcceptanceStatus) => r.status === 'Pending'
        );
        this._hasPending.set(hasPending);
        this._acceptanceChecked.set(true);
        return hasPending;
      }),
      catchError(() => {
        return of(false);
      })
    );
  };

  // Call this after patient accepts — marks accepted in memory so guard immediately permits dashboard navigation
  markAsAccepted = (): void => {
    this._hasPending.set(false);
    this._acceptanceChecked.set(true);
  };

  // Call this on logout — clears cache so guard re-checks next navigation
  clearAcceptanceCache = (): void => {
    this._acceptanceChecked.set(false);
    this._hasPending.set(false);
  };

  acceptLegalDocuments = (legalDocumentIds: string[]): Observable<ApiResponse<any>> => {
    const body: IAcceptLegalDocumentsRequest = { legalDocumentIds };
    return this.httpClient.post<ApiResponse<any>>(
      PatientLegalAcceptanceEndpoints.LegalAcceptance,
      body
    ).pipe(
      tap(() => this.markAsAccepted())  // mark as accepted immediately so guard permits dashboard navigation
    );
  };

  getActiveDocuments = (): Observable<ApiResponse<IActiveLegalDocument[]>> =>
    this.httpClient.get<ApiResponse<IActiveLegalDocument[]>>(
      PatientLegalAcceptanceEndpoints.ActiveDocuments
    );

  getDownloadUrl = (legalDocumentIdEnc: string): string => {
    return `${environment.apiEndpoint}/master/legal-documents/${legalDocumentIdEnc}/download`;
  };

  downloadLegalDocument = (idEnc: string): Observable<Blob> =>
    this.httpClient.get(this.getDownloadUrl(idEnc), { responseType: 'blob' });
}